<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-lg font-semibold text-gray-900">Clubs Management</h2>
        <p class="text-sm text-gray-500">Organize your email lists into clubs for better management</p>
      </div>
      <button
        @click="openCreateModal"
        class="btn-primary"
      >
        <PlusIcon class="w-4 h-4 mr-2" />
        Add Club
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-md p-4">
      <div class="flex">
        <ExclamationTriangleIcon class="h-5 w-5 text-red-400" />
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">Error loading clubs</h3>
          <p class="mt-1 text-sm text-red-700">{{ error }}</p>
        </div>
      </div>
    </div>

    <!-- Clubs List -->
    <div v-else-if="clubs.length > 0" class="bg-white shadow-sm rounded-lg">
      <div class="px-6 py-4 border-b border-gray-200">
        <h3 class="text-lg font-medium text-gray-900">Your Clubs</h3>
      </div>
      <div class="divide-y divide-gray-200">
        <div
          v-for="club in clubs"
          :key="club.ClubID"
          class="px-6 py-4 hover:bg-gray-50"
        >
          <div class="flex items-center justify-between">
            <div class="flex-1">
              <h4 class="text-sm font-medium text-gray-900">{{ club.ClubName }}</h4>
              <p class="text-sm text-gray-500">
                {{ club.ListCount }} {{ club.ListCount === 1 ? 'list' : 'lists' }}
                • Created {{ formatDate(club.CreatedAt) }}
              </p>
            </div>
            <div class="flex items-center space-x-2">
              <button
                @click="openEditModal(club)"
                class="text-gray-400 hover:text-gray-600"
                title="Edit club"
              >
                <PencilIcon class="w-4 h-4" />
              </button>
              <button
                @click="confirmDelete(club)"
                class="text-gray-400 hover:text-red-600"
                title="Delete club"
                :disabled="club.ListCount > 0"
              >
                <TrashIcon class="w-4 h-4" :class="{ 'opacity-50 cursor-not-allowed': club.ListCount > 0 }" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-12">
      <UsersIcon class="mx-auto h-12 w-12 text-gray-400" />
      <h3 class="mt-2 text-sm font-medium text-gray-900">No clubs yet</h3>
      <p class="mt-1 text-sm text-gray-500">Get started by creating your first club.</p>
      <div class="mt-6">
        <button
          @click="openCreateModal"
          class="btn-primary"
        >
          <PlusIcon class="w-4 h-4 mr-2" />
          Add Club
        </button>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeModal"></div>

        <span class="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form @submit.prevent="saveClub">
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div class="sm:flex sm:items-start">
                <div class="w-full">
                  <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                    {{ editingClub ? 'Edit Club' : 'Create New Club' }}
                  </h3>
                  
                  <div>
                    <label for="clubName" class="block text-sm font-medium text-gray-700">
                      Club Name
                    </label>
                    <input
                      id="clubName"
                      v-model="formData.clubName"
                      type="text"
                      required
                      class="mt-1 input-field"
                      placeholder="Enter club name"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                :disabled="saving || !formData.clubName.trim()"
                class="btn-primary sm:ml-3 sm:w-auto"
              >
                <span v-if="saving" class="flex items-center">
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {{ editingClub ? 'Updating...' : 'Creating...' }}
                </span>
                <span v-else>{{ editingClub ? 'Update Club' : 'Create Club' }}</span>
              </button>
              <button
                type="button"
                @click="closeModal"
                class="btn-secondary sm:mt-0 sm:ml-3 sm:w-auto"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeDeleteModal"></div>

        <span class="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <ExclamationTriangleIcon class="h-6 w-6 text-red-600" />
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                  Delete Club
                </h3>
                <div class="mt-2">
                  <p class="text-sm text-gray-500">
                    Are you sure you want to delete "{{ clubToDelete?.ClubName }}"? This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              @click="deleteClub"
              :disabled="deleting"
              class="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 sm:ml-3 sm:w-auto"
            >
              <span v-if="deleting" class="flex items-center">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Deleting...
              </span>
              <span v-else>Delete</span>
            </button>
            <button
              @click="closeDeleteModal"
              class="btn-secondary sm:mt-0 sm:ml-3 sm:w-auto"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useClubsStore } from '../stores/clubs'
import { toast } from 'vue3-toastify'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UsersIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline'

const clubsStore = useClubsStore()

// State
const showModal = ref(false)
const showDeleteModal = ref(false)
const editingClub = ref(null)
const clubToDelete = ref(null)
const saving = ref(false)
const deleting = ref(false)

// Form data
const formData = ref({
  clubName: ''
})

// Computed
const clubs = computed(() => clubsStore.clubs)
const loading = computed(() => clubsStore.loading)
const error = computed(() => clubsStore.error)

// Methods
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString()
}

const openCreateModal = () => {
  editingClub.value = null
  formData.value = { clubName: '' }
  showModal.value = true
}

const openEditModal = (club) => {
  editingClub.value = club
  formData.value = { clubName: club.ClubName }
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  editingClub.value = null
  formData.value = { clubName: '' }
}

const confirmDelete = (club) => {
  if (club.ListCount > 0) {
    toast.error('Cannot delete club with active email lists')
    return
  }
  clubToDelete.value = club
  showDeleteModal.value = true
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  clubToDelete.value = null
}

const saveClub = async () => {
  try {
    saving.value = true
    
    if (editingClub.value) {
      await clubsStore.updateClub(editingClub.value.ClubID, formData.value)
      toast.success('Club updated successfully')
    } else {
      await clubsStore.createClub(formData.value)
      toast.success('Club created successfully')
    }
    
    closeModal()
  } catch (error) {
    console.error('Error saving club:', error)
    // Error toast is handled by the store
  } finally {
    saving.value = false
  }
}

const deleteClub = async () => {
  try {
    deleting.value = true
    
    await clubsStore.deleteClub(clubToDelete.value.ClubID)
    toast.success('Club deleted successfully')
    
    closeDeleteModal()
  } catch (error) {
    console.error('Error deleting club:', error)
    // Error toast is handled by the store
  } finally {
    deleting.value = false
  }
}

// Lifecycle
onMounted(async () => {
  await clubsStore.fetchClubs()
})
</script> 